import { includes, values } from "ramda";
import { nodeErrorMessage } from "../formsviewer/validation";
import { getFileProperties } from "../utility/fileHelpers";
import { xmlToString } from "../xmlUtility";
import { XNodeSet, XPathContext, XPathParser, XString, argError, emptyXString, falseXBoolean, trueXBoolean } from "./xpath.function.helpers";
import { ATTACHMENTLINKMODE_BROWSER, ATTACHMENTLINKMODE_DEFAULT, ATTACHMENTLINKMODE_NORMAL, KEY_LISTFORM_ATTACHMENTLINKMODE } from "../options/options.listform";


export const namespaceFv = "http://www.qdabra.com/FormsViewer/xslt";

const sizeSuffixes = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

const FUNC_NAME_LISTFORM_ATTACHMENTURL = 'list-form-attachment-url';


function fileProp(params, functionName, propertyName) {
    const attachment = params[0];

    if (params.length !== 1) {
        argError(functionName, "nodeset");
    }

    const props = getFileProperties(attachment.first());

    return new XString(props[propertyName]);
}

function fileName(_, params) {
    return fileProp(params, "file-name", "name");
}

function fileSize(_, params) {
    return fileProp(params, "file-size", "size");
}

function formatFileSize(c, params) {
    var size,
        magnitude,
        adjusted,
        formatted;

    if (params.length !== 1) {
        argError("format-file-size", "number");
    }

    size = params[0].numberValue();

    if (size !== size || size < 0) {
        formatted = "N/A";
    } else if (size < 1024) {
        formatted = size + " bytes";
    } else {
        magnitude = ~~(Math.log(size) / Math.log(1024));
        adjusted = size / (1 << (magnitude * 10));
        formatted = adjusted.toFixed(1) + " " + sizeSuffixes[magnitude];
    }

    return new XString(formatted);
}

function truncateFileNameInner(fileName, maxLength) {
    if (fileName.length <= maxLength) {
        return fileName;
    }

    const nameSegments = fileName.split('.');
    const extension = nameSegments.length > 1 ? nameSegments[nameSegments.length - 1] : "";

    // Edge case TODO: extension is unusually long
    return fileName.substring(0, maxLength - extension.length - 3) + "..." + extension;
}

function truncateFileName(c, params) {
    const fileNameParam = params[0];
    const maxLengthParam = params[1];

    if (params.length !== 2 || Number.isNaN(maxLengthParam.numberValue())) {
        argError("truncate-file-name", "string, number");
    }

    return new XString(truncateFileNameInner(
        fileNameParam.stringValue(),
        maxLengthParam.numberValue(),
    ));
}

function datePart(_, params) {
    const value = params[0].stringValue();
    const date = new Date(value);

    if (!Number.isNaN(date.getDate())) {
        //date = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
        return new XString(date.toISOString().substring(0, 10));
    }

    return new XString(value);
}

function uniqueValues(c, params) {
    const nodes = params[0].toArray();
    const path = params[1].stringValue();

    const parser = new XPathParser();
    const expr = parser.parse(path);

    let items = {};

    for (const node of nodes) {
        const execContext = new XPathContext(c.variableResolver, c.namespaceResolver, c.functionResolver);
        execContext.expressionContextNode = node;

        const val = expr.evaluate(execContext).stringValue();

        if (!(val in items)) {
            items[val] = node;
        }
    }

    let nodeSet = new XNodeSet();
    nodeSet.addArray(values(items));

    return nodeSet;
}

function xmlString(_, params) {
    const nodes = params[0].toArray();

    const value = nodes.map(xmlToString).join('');

    return new XString(value);
}


const validationError = (_, params) => {
    if (params.length < 1) {
        argError('validation-error', 'nodeset');
    }

    const [nodeSet] = params;
    const node = nodeSet.first();

    return new XString(nodeErrorMessage(node));
};

const getListAttachmentBrowserUrl = (url) => {
    const paramSeparator = includes('?', url) ? '&' : '?';

    return `${url}${paramSeparator}web=1`;
};

const getAttachmentUrl = (url, settings) => {
    const urlMode =
        settings.getOption(KEY_LISTFORM_ATTACHMENTLINKMODE) || ATTACHMENTLINKMODE_DEFAULT;

    switch (urlMode) {
        case ATTACHMENTLINKMODE_BROWSER:
            return getListAttachmentBrowserUrl(url);
        case ATTACHMENTLINKMODE_NORMAL:
        case ATTACHMENTLINKMODE_DEFAULT:
            return url;
        default:
            console.warn('Unknown attachment link mode: ' + urlMode);
            return url;
    }
};

const listFormAttachmentUrl = (settings) => (_, params) => {
    if (params.length !== 1) {
        argError(FUNC_NAME_LISTFORM_ATTACHMENTURL, 'string');
    }

    const url = params[0].stringValue();

    return new XString(getAttachmentUrl(url, settings));
}

const makeFvXsltFunctions = (
    characterLimitSource,
    viewManager,
    settings,
) => {
    // (DOMNode, String) -> CharacterLimit
    const getCharacterLimit = (node, ctrlId) =>
        !(node && ctrlId)
            ? null
            : characterLimitSource.getCharacterLimit(
                viewManager.getCurrentView(),
                node,
                ctrlId,
            );

    // (String, [DOMNode, String], String) -> X
    const controlLimitFunc = (name, params, propName) => {
        if (params.length !== 2) {
            argError(name, 'NodeSet, string');
        }

        const [nodes, ctrlId] = params;

        const limit = getCharacterLimit(
            nodes.first(),
            ctrlId.stringValue(),
        );

        return limit && limit[propName];
    }

    const getMaxLength = (_, params) => {
        const maxLength = controlLimitFunc('max-length', params, 'maxLength');

        return (typeof maxLength === 'number')
            ? new XString(maxLength.toString())
            : emptyXString;
    };


    const hasMoveNext = (_, params) =>
        controlLimitFunc('has-move-next', params, 'moveNext')
            ? trueXBoolean
            : falseXBoolean;

    return {
        'date-part': datePart,
        'file-name': fileName,
        'file-size': fileSize,
        'format-file-size': formatFileSize,
        [FUNC_NAME_LISTFORM_ATTACHMENTURL]: listFormAttachmentUrl(settings),
        'has-move-next': hasMoveNext,
        'max-length': getMaxLength,
        'truncate-file-name': truncateFileName,
        'unique-values': uniqueValues,
        'validation-error': validationError,
        'xml-string': xmlString,
    };
}


export default makeFvXsltFunctions;